import { type BundledLanguage } from "shiki";

let highlighterPromise: ReturnType<typeof import("shiki").createHighlighter> | null = null;

const loadedLangs = new Set<string>();

async function getHighlighter() {
  if (!highlighterPromise) {
    const { createHighlighter } = await import("shiki");
    highlighterPromise = createHighlighter({
      themes: ["github-dark-default"],
      langs: [], // Start empty — load languages on demand
    });
  }
  return highlighterPromise;
}

/**
 * Highlight a code string with Shiki. Returns HTML with inline theme styles.
 * Languages are lazy-loaded on first use.
 */
export async function highlightCode(code: string, lang: string): Promise<string> {
  const highlighter = await getHighlighter();
  const resolvedLang = lang || "text";

  if (!loadedLangs.has(resolvedLang)) {
    try {
      await highlighter.loadLanguage(resolvedLang as BundledLanguage);
      loadedLangs.add(resolvedLang);
    } catch {
      // Unknown language — fall back to plaintext
    }
  }

  return highlighter.codeToHtml(code, {
    lang: loadedLangs.has(resolvedLang) ? resolvedLang : "text",
    theme: "github-dark-default",
  });
}

/**
 * Post-process HTML: find all <pre><code class="language-XXX"> blocks
 * and replace them with Shiki-highlighted versions.
 * Returns the full HTML string with highlighted code blocks.
 */
export async function highlightAllCodeBlocks(html: string): Promise<string> {
  await getHighlighter();

  const regex = /<pre><code(?: class="language-(\w+)")?>([\s\S]*?)<\/code><\/pre>/g;

  const replacements: Promise<{ index: number; length: number; replacement: string }>[] = [];

  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    const lang = match[1] || "text";
    const rawCode = match[2]
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    const idx = match.index;
    const len = match[0].length;

    replacements.push(
      highlightCode(rawCode, lang).then((highlighted) => ({
        index: idx,
        length: len,
        replacement: `<div class="code-block-wrapper" data-lang="${lang}">${highlighted}</div>`,
      }))
    );
  }

  const results = await Promise.all(replacements);
  results.sort((a, b) => b.index - a.index);

  let result = html;
  for (const r of results) {
    result = result.slice(0, r.index) + r.replacement + result.slice(r.index + r.length);
  }

  return result;
}

/**
 * Cross-browser idle callback wrapper.
 * Falls back to setTimeout when requestIdleCallback is unavailable (Safari, older browsers).
 * Prevents TypeError console errors that tank Lighthouse Best Practices scores.
 */
export function scheduleIdle(cb: () => void, opts?: { timeout: number }): void {
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(cb, opts);
  } else {
    setTimeout(cb, opts?.timeout ?? 1);
  }
}

/**
 * Compute estimated reading time in minutes.
 * Strips HTML tags before counting words.
 */
export function readingTime(text: string): number {
  const plainText = text.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  const words = plainText.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}
